import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {ConjuntoCategoriaContabil} from '../../../../core/conjunto-categoria-contabil';
import {ConjuntoCategoriaContabilService} from '../../../../services/conjunto-categoria-contabil/conjunto-categoria-contabil.service';
import {ToastService} from '../../../../services/toast/toast.service';
import {ConfiguracaoSeletor} from '../../../shared/interfaces/opcao-selecao.interface';
import {ConfirmDialogComponent} from '../../../common/confirm-dialog/confirm-dialog.component';
import {PlanosContas} from '../../../../core/planos-contas';
import {TipoLancamentoExtrato} from '../../../../core/tipo-lancamento-extrato';
import {TipoLancamentoExtratoService} from '../../../../services/tipo-lancamento-extrato/tipo-lancamento-extrato.service';

@Component({
    selector: 'app-cadastrar-argumento-dialog',
    templateUrl: './cadastrar-conjunto-categoria-contabil-dialog.component.html',
    styleUrls: ['./cadastrar-conjunto-categoria-contabil-dialog.component.css']
})
export class CadastrarConjuntoCategoriaContabilDialogComponent implements OnInit {

    form: FormGroup;
    argumento: ConjuntoCategoriaContabil = new ConjuntoCategoriaContabil();
    argumentoExistente: ConjuntoCategoriaContabil | null = null;
    comboPlanosContas: PlanosContas[] = [];
    comboTiposLancamento: TipoLancamentoExtrato[] = [];
    isLoading: boolean = false;
    isEdicao: boolean = false;
    buscandoArgumento: boolean = false;
    categoriasDesabilitadas: boolean = false;

    tipoTransacao: string; // D ou C
    categoriaContabilArquivo: number; // ID da categoria contábil do arquivo
    categoriaOrigemDesabilitada: boolean = false;
    categoriaDestinoDesabilitada: boolean = false;

    // Configuração para o seletor genérico
    configuracaoSeletorCategoria: ConfiguracaoSeletor = {
        placeholder: 'Conta Contábil *',
        mostrarOpcaoVazia: false,
        obrigatorio: true,
        campoExibicao: 'planoConta',
        campoValor: 'id',
        habilitarPesquisa: true,
        camposPesquisa: ['planoConta'],
        tamanhoMinimoPesquisa: 1,
        panelClass: 'combo-generico-panel'
    };

    // Configuração do seletor de tipo de importação (exibe somente a descrição do tipo)
    configuracaoSeletorTipoLancamento: ConfiguracaoSeletor = {
        mostrarOpcaoVazia: true,
        textoOpcaoVazia: 'Nenhum',
        obrigatorio: false,
        campoExibicao: 'descricao',
        campoValor: 'id',
        habilitarPesquisa: false,
        panelClass: 'combo-generico-panel'
    };

    constructor(
        private dialogRef: MatDialogRef<CadastrarConjuntoCategoriaContabilDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private formBuilder: FormBuilder,
        private argumentoCategoriaContabilService: ConjuntoCategoriaContabilService,
        private toastService: ToastService,
        private dialog: MatDialog,
        private tipoLancamentoExtratoService: TipoLancamentoExtratoService
    ) {
        // Receber dados da linha atual
        if (data) {
            this.comboPlanosContas = data.planosContas || [];
            this.comboTiposLancamento = data.tiposLancamento || [];
            this.tipoTransacao = data.tipoTransacao || '';
            this.categoriaContabilArquivo = data.categoriaContabilArquivo || null;

            // Pré-preencher com dados da linha
            const descricaoTransacao = data.descricaoTransacao || '';
            let categoriaOrigem = data.idCategoriaOrigem || null;
            let categoriaDestino = data.idCategoriaDestino || null;

            // Determinar qual campo deve ser fixado baseado no tipo de transação
            if (this.tipoTransacao === 'D' && this.categoriaContabilArquivo) {
                categoriaOrigem = this.categoriaContabilArquivo;
                this.categoriaOrigemDesabilitada = true;
            } else if (this.tipoTransacao === 'C' && this.categoriaContabilArquivo) {
                categoriaDestino = this.categoriaContabilArquivo;
                this.categoriaDestinoDesabilitada = true;
            }

            this.inicializarFormulario(descricaoTransacao, categoriaOrigem, categoriaDestino);
        } else {
            this.inicializarFormulario('', null, null);
        }
    }

    ngOnInit(): void {
        // Carregar os tipos de importação caso não tenham sido informados pelo componente pai
        if (this.comboTiposLancamento.length === 0) {
            this.carregarTiposLancamento();
        }

        // Se já tiver ambas as categorias pré-preenchidas, buscar argumento existente
        const categoriaOrigem = this.form.get('idCategoriaOrigem')?.value;
        const categoriaDestino = this.form.get('idCategoriaDestino')?.value;
        if (categoriaOrigem && categoriaDestino) {
            // Aguardar um pouco para garantir que o formulário está inicializado
            setTimeout(() => {
                this.buscarArgumentoExistente();
            }, 100);
        }
    }

    carregarTiposLancamento() {
        this.tipoLancamentoExtratoService.getAll().subscribe((tipos: TipoLancamentoExtrato[]) => {
            this.comboTiposLancamento = tipos || [];
        });
    }

    inicializarFormulario(descricaoTransacao: string, categoriaOrigem: number | null, categoriaDestino: number | null) {
        this.form = this.formBuilder.group({
            idCategoriaOrigem: [categoriaOrigem, Validators.required],
            idCategoriaDestino: [categoriaDestino, Validators.required],
            argumentos: this.formBuilder.array([], this.validarPeloMenosUmArgumento),
            idTipoLancamento: [null],
            ativo: [true]
        });

        // Adicionar descrição da transação como primeiro argumento se existir
        if (descricaoTransacao && descricaoTransacao.trim()) {
            const argumentosArray = this.getArgumentosFormArray();
            this.adicionarControlComListener(argumentosArray, descricaoTransacao.trim());
        } else {
            this.adicionarLinhaArgumento();
        }
    }

    validarPeloMenosUmArgumento(control: AbstractControl): ValidationErrors | null {
        const argumentosArray = control as FormArray;
        if (!argumentosArray || !argumentosArray.controls) {
            return { peloMenosUmArgumento: true };
        }

        const argumentosValidos = argumentosArray.controls
            .map(ctrl => ctrl.value?.trim())
            .filter((arg: string) => {
                if (!arg || arg.length < 5) return false;
                // Remover wildcards e verificar se sobra texto suficiente
                const textoSemWildcards = arg.replace(/\{\*}/g, '').trim();
                return textoSemWildcards.length >= 5;
            });

        return argumentosValidos.length > 0 ? null : { peloMenosUmArgumento: true };
    }

    validarTextoComWildcard(control: AbstractControl): ValidationErrors | null {
        const valor = control.value?.trim() || '';

        // Se estiver vazio, não validar aqui (será validado pelo minlength)
        if (!valor) {
            return null;
        }

        // Remover todos os wildcards {*} do texto
        const textoSemWildcards = valor.replace(/\{\*}/g, '').trim();

        // Se após remover os wildcards não sobrar pelo menos 5 caracteres, é inválido
        if (textoSemWildcards.length < 5) {
            return { apenasWildcards: true };
        }

        return null;
    }

    adicionarControlComListener(argumentosArray: FormArray, valor: string) {
        const control = this.formBuilder.control(valor, [
            Validators.minLength(5),
            Validators.maxLength(255),
            this.validarTextoComWildcard.bind(this)
        ]);
        argumentosArray.push(control);
        // Adicionar listener para revalidar o FormArray quando o controle mudar
        control.valueChanges.subscribe(() => {
            argumentosArray.updateValueAndValidity();
        });
    }

    salvar() {
        // Validar se há pelo menos um argumento válido
        const argumentosArray = this.form.get('argumentos') as FormArray;
        const argumentosValidos = argumentosArray.controls
            .map(control => control.value?.trim())
            .filter((arg: string) => {
                if (!arg || arg.length < 5) return false;
                // Remover wildcards e verificar se sobra texto suficiente
                const textoSemWildcards = arg.replace(/\{\*}/g, '').trim();
                return textoSemWildcards.length >= 5;
            });

        if (argumentosValidos.length === 0) {
            this.toastService.showAlerta('É necessário adicionar pelo menos um argumento com no mínimo 5 caracteres (sem contar apenas wildcards).');
            return;
        }

        if (this.form.valid) {
            this.isLoading = true;

            this.argumento.idCategoriaOrigem = this.form.get('idCategoriaOrigem')?.value;
            this.argumento.idCategoriaDestino = this.form.get('idCategoriaDestino')?.value;
            this.argumento.argumentos = argumentosValidos;
            this.argumento.ativo = this.form.get('ativo')?.value;
            this.argumento.idTipoLancamento = this.form.get('idTipoLancamento')?.value ?? null;

            if (this.isEdicao && this.argumento.id) {
                // Modo edição - usar alterar
                this.argumentoCategoriaContabilService.alterar(this.argumento).subscribe({
                    next: () => {
                        this.isLoading = false;
                        this.toastService.showSucesso('Argumento alterado com sucesso!');
                        // Retornar o argumento alterado para o componente pai
                        this.dialogRef.close(this.argumento);
                    },
                    error: (error) => {
                        this.isLoading = false;
                        this.toastService.showAlerta('Erro ao alterar argumento: ' + (error.error?.mensagem || 'Erro desconhecido'));
                    }
                });
            } else {
                // Modo cadastro - usar cadastrar
                this.argumentoCategoriaContabilService.cadastrar(this.argumento).subscribe({
                    next: () => {
                        this.isLoading = false;
                        this.toastService.showSucesso('Argumento cadastrado com sucesso!');
                        // Retornar o argumento criado para o componente pai
                        // O backend retorna void, então retornamos o objeto que foi enviado
                        this.dialogRef.close(this.argumento);
                    },
                    error: (error) => {
                        this.isLoading = false;
                        this.toastService.showAlerta('Erro ao cadastrar argumento: ' + (error.error?.mensagem || 'Erro desconhecido'));
                    }
                });
            }
        } else {
            this.toastService.showAlerta('Por favor, preencha todos os campos obrigatórios.');
        }
    }

    cancelar() {
        this.dialogRef.close(null);
    }

    getErrorMessage(fieldName: string): string {
        const field = this.form.get(fieldName);
        if (field?.hasError('required')) {
            return 'Este campo é obrigatório';
        }
        if (field?.hasError('minlength')) {
            return 'Este campo deve ter no mínimo 5 caracteres';
        }
        if (field?.hasError('maxlength')) {
            return 'Este campo excede o limite de 255 caracteres';
        }
        return '';
    }

    getArgumentosFormArray(): FormArray {
        return this.form.get('argumentos') as FormArray;
    }

    adicionarLinhaArgumento() {
        const argumentosArray = this.getArgumentosFormArray();
        this.adicionarControlComListener(argumentosArray, '');
        // Revalidar o FormArray após adicionar
        argumentosArray.updateValueAndValidity();
    }

    removerLinhaArgumento(index: number) {
        const argumentosArray = this.getArgumentosFormArray();
        if (argumentosArray.length > 1) {
            argumentosArray.removeAt(index);
        } else {
            // Se for a última linha, apenas limpar o valor ao invés de remover
            argumentosArray.at(index).setValue('');
        }
        // Revalidar o FormArray após remover
        argumentosArray.updateValueAndValidity();
    }

    inserirWildcard(index: number, inputElement: HTMLInputElement) {
        const argumentoControl = this.getArgumentosFormArray().at(index);
        const valorAtual = argumentoControl.value || '';
        const start = inputElement.selectionStart || valorAtual.length;
        const end = inputElement.selectionEnd || valorAtual.length;
        const wildcard = '{*}';

        // Inserir o wildcard na posição do cursor
        const novoValor = valorAtual.substring(0, start) + wildcard + valorAtual.substring(end);
        argumentoControl.setValue(novoValor);

        // Reposicionar o cursor após o wildcard inserido
        setTimeout(() => {
            inputElement.focus();
            const novaPosicao = start + wildcard.length;
            inputElement.setSelectionRange(novaPosicao, novaPosicao);
        }, 0);

        // Revalidar o FormArray após inserir wildcard
        const argumentosArray = this.getArgumentosFormArray();
        argumentosArray.updateValueAndValidity();
    }

    onCategoriaOrigemChange(event: any) {
        if (this.categoriaOrigemDesabilitada) {
            return;
        }

        // Só permite alterar se as categorias não estiverem desabilitadas (modo edição)
        if (!this.categoriasDesabilitadas) {
            this.form.get('idCategoriaOrigem')?.setValue(event);
            this.buscarArgumentoExistente();
        }
    }

    onTipoLancamentoChange(event: any) {
        this.form.get('idTipoLancamento')?.setValue(event ?? null);
    }

    onCategoriaDestinoChange(event: any) {
        if (this.categoriaDestinoDesabilitada) {
            return;
        }

        // Só permite alterar se as categorias não estiverem desabilitadas (modo edição)
        if (!this.categoriasDesabilitadas) {
            this.form.get('idCategoriaDestino')?.setValue(event);
            this.buscarArgumentoExistente();
        }
    }

    buscarArgumentoExistente() {
        const categoriaOrigem = this.form.get('idCategoriaOrigem')?.value;
        const categoriaDestino = this.form.get('idCategoriaDestino')?.value;

        // Só busca se ambas as categorias estiverem selecionadas
        if (categoriaOrigem && categoriaDestino) {
            this.buscandoArgumento = true;
            this.argumentoCategoriaContabilService.getByCategorias(categoriaOrigem, categoriaDestino).subscribe({
                next: (argumentoExistente: ConjuntoCategoriaContabil) => {
                    this.buscandoArgumento = false;
                    if (argumentoExistente) {
                        // Argumento encontrado - perguntar se quer criar novo ou alterar
                        this.argumentoExistente = argumentoExistente;
                        this.perguntarCriarOuAlterar();
                    } else {
                        // Argumento não encontrado - modo cadastro
                        this.argumentoExistente = null;
                        this.isEdicao = false;
                        this.categoriasDesabilitadas = false;
                        this.argumento = new ConjuntoCategoriaContabil();
                    }
                },
                error: (error) => {
                    this.buscandoArgumento = false;
                    console.error('Erro ao buscar argumento existente:', error);
                    // Em caso de erro, assume modo cadastro
                    this.argumentoExistente = null;
                    this.isEdicao = false;
                    this.categoriasDesabilitadas = false;
                }
            });
        } else {
            // Se não tiver ambas as categorias, assume modo cadastro
            this.argumentoExistente = null;
            this.isEdicao = false;
            this.categoriasDesabilitadas = false;
            this.argumento = new ConjuntoCategoriaContabil();
        }
    }

    perguntarCriarOuAlterar() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
            pergunta: 'Já existe um argumento cadastrado com este conjunto de categorias contábeis. Deseja criar um novo ou alterar o existente?',
            textoConfirma: 'ALTERAR EXISTENTE',
            textoCancela: 'CRIAR NOVO',
            textoAuxiliar: 'Nota: Não é permitido ter dois argumentos com o mesmo conjunto de categorias. Se escolher criar novo, será necessário alterar as categorias.'
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((alterar: boolean) => {
            if (alterar) {
                // Modo edição - carregar argumento existente e desabilitar categorias
                this.isEdicao = true;
                this.categoriasDesabilitadas = true;
                this.argumento = this.argumentoExistente!;
                this.carregarArgumentoNoFormulario(this.argumentoExistente!);
                this.toastService.showSucesso('Argumento existente carregado para edição. As categorias contábeis não podem ser alteradas.');
            } else {
                // Modo cadastro - limpar categorias e usar descrição da transação como primeiro argumento
                this.isEdicao = false;
                this.categoriasDesabilitadas = false;
                this.argumento = new ConjuntoCategoriaContabil();

                switch (this.tipoTransacao) {
                    case 'C':
                        this.form.patchValue({ idCategoriaOrigem: null });
                        break;
                    case 'D':
                        this.form.patchValue({ idCategoriaDestino: null });
                        break;
                }
                this.form.patchValue({ idTipoLancamento: null });

                // Limpar FormArray de argumentos
                const argumentosArray = this.form.get('argumentos') as FormArray;
                while (argumentosArray.length !== 0) {
                    argumentosArray.removeAt(0);
                }

                // Adicionar descrição da transação como primeiro argumento se existir
                const descricaoTransacao = this.data?.descricaoTransacao || '';
                if (descricaoTransacao && descricaoTransacao.trim()) {
                    this.adicionarControlComListener(argumentosArray, descricaoTransacao.trim());
                } else {
                    // Se não houver descrição, adicionar uma linha vazia
                    this.adicionarLinhaArgumento();
                }
            }
        });
    }

    carregarArgumentoNoFormulario(argumento: ConjuntoCategoriaContabil) {
        // Limpar FormArray existente
        const argumentosArray = this.form.get('argumentos') as FormArray;
        while (argumentosArray.length !== 0) {
            argumentosArray.removeAt(0);
        }

        // Adicionar argumentos existentes
        const argumentos = argumento.argumentos || [];
        const descricaoTransacao = this.data?.descricaoTransacao || '';
        const descricaoTransacaoTrim = descricaoTransacao ? descricaoTransacao.trim() : '';

        if (argumentos.length > 0) {
            // Adicionar argumentos existentes
            argumentos.forEach(arg => {
                this.adicionarControlComListener(argumentosArray, arg);
            });

            // Verificar se a descrição da transação já existe na lista (comparação case-insensitive)
            if (descricaoTransacaoTrim) {
                const descricaoExiste = argumentos.some(arg =>
                    arg && arg.trim().toLowerCase() === descricaoTransacaoTrim.toLowerCase()
                );

                // Se não existir, adicionar a descrição da transação
                if (!descricaoExiste) {
                    this.adicionarControlComListener(argumentosArray, descricaoTransacaoTrim);
                }
            }
        } else {
            // Se não houver argumentos, verificar se há descrição da transação pré-preenchida
            if (descricaoTransacaoTrim) {
                this.adicionarControlComListener(argumentosArray, descricaoTransacaoTrim);
            } else {
                // Se não houver argumentos nem descrição, adicionar uma linha vazia
                this.adicionarLinhaArgumento();
            }
        }

        // Atualizar valores do formulário
        this.form.patchValue({
            idCategoriaOrigem: argumento.idCategoriaOrigem,
            idCategoriaDestino: argumento.idCategoriaDestino,
            idTipoLancamento: argumento.idTipoLancamento ?? null,
            ativo: argumento.ativo !== undefined ? argumento.ativo : true
        });
    }
}

